import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import tabliyaLogo from '../../../images/label_icons/logo_black.png';
import tabliyaLogoColor from '../../../images/tabliya_logo_print.png';
import halalLogo from '../../../images/label_icons/halal_logo.png';
import microwaveIcon from '../../../images/label_icons/microwave_black.png';
import ovenFanFunctionIcon from '../../../images/label_icons/oven_fan_function_black.png';
import ovenFanGrillIcon from '../../../images/label_icons/oven_fan_grill_black.png';
import ovenFullGrillIcon from '../../../images/label_icons/oven_full_grill_black.png';
import ovenLowerHeatIcon from '../../../images/label_icons/oven_lower_heat_black.png';
import ovenUpperLowerHeatIcon from '../../../images/label_icons/oven_upper_and_lower_heat_black.png';
import ovenUpperHeatIcon from '../../../images/label_icons/oven_upper_heat_black.png';
import panIcon from '../../../images/label_icons/pan.png';
import { downloadElementAsPng } from '../../../utils/domExport';
import { getRecipeLabel, updateRecipeLabel } from '../../../services/RecipesService';
import { LETTERS as NS_LETTERS, NS_COLORS, NS_TEXT_COLORS } from './NutriScoreSection';
import {
  buildPrintableLabelStorageKey,
  PRINTABLE_LABEL_LOCALES,
  PRINTABLE_LABEL_COMPANY_DETAILS,
  buildLabelDownloadFileName,
  buildPrintableLabelInitialState,
  calculatePrintableLabelNutritionData,
  computeRecipeFoodWeightG,
  createCookingMethod,
  createMaterialsById,
  DEFAULT_LABEL_SECTION_ORDER,
  formatPrintableNutrition,
  mergePrintableLabelState,
  recipeHasFoodItems,
  sanitizeSectionOrder,
} from './recipePrintableLabelUtils';

const ICON_OPTIONS = [
  { key: 'pan', label: 'Pan', src: panIcon },
  { key: 'microwave', label: 'Microwave', src: microwaveIcon },
  { key: 'oven_fan_function', label: 'Oven fan function', src: ovenFanFunctionIcon },
  { key: 'oven_fan_grill', label: 'Oven fan grill', src: ovenFanGrillIcon },
  { key: 'oven_full_grill', label: 'Oven full grill', src: ovenFullGrillIcon },
  { key: 'oven_lower_heat', label: 'Oven lower heat', src: ovenLowerHeatIcon },
  { key: 'oven_upper_and_lower_heat', label: 'Oven upper and lower heat', src: ovenUpperLowerHeatIcon },
  { key: 'oven_upper_heat', label: 'Oven upper heat', src: ovenUpperHeatIcon },
];

const ICON_MAP = ICON_OPTIONS.reduce((acc, option) => {
  acc[option.key] = option;
  return acc;
}, {});

const PRINTABLE_NUTRISCORE_MODES = {
  BLACK_AND_WHITE: 'blackAndWhite',
  COLOR: 'color',
};

function NutriScoreLabelStrip({ letter, label, mode = PRINTABLE_NUTRISCORE_MODES.BLACK_AND_WHITE }) {
  const isColorMode = mode === PRINTABLE_NUTRISCORE_MODES.COLOR;

  return (
    <div
      className={`recipe-print-label__nutriscore${isColorMode ? ' recipe-print-label__nutriscore--color' : ''}`}
      aria-label={`${label} ${letter}`}
    >
      <div className="recipe-print-label__nutriscore-title">{label}</div>
      <div className="recipe-print-label__nutriscore-strip">
        {NS_LETTERS.map((l) => {
          const active = l === letter;
          const colorStyle = isColorMode
            ? {
                background: NS_COLORS[l],
                color: NS_TEXT_COLORS[l],
                borderColor: active ? 'rgba(0, 0, 0, 0.18)' : 'transparent',
                borderRadius: active ? 5 : 3,
                boxShadow: active ? '0 2px 8px rgba(0, 0, 0, 0.25)' : 'none',
              }
            : undefined;
          return (
            <div
              key={l}
              className={`recipe-print-label__nutriscore-letter${active ? ' recipe-print-label__nutriscore-letter--active' : ''}`}
              style={colorStyle}
            >
              {l}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PrintableRecipeLabel = React.forwardRef(function PrintableRecipeLabel(
  { locale, value, nutritionData, recipeName, servingLabel, nutriScore, nutriScoreMode },
  ref
) {
  const copy = PRINTABLE_LABEL_LOCALES[locale] || PRINTABLE_LABEL_LOCALES.en;
  const isColorMode = nutriScoreMode === PRINTABLE_NUTRISCORE_MODES.COLOR;
  const nutritionSection = formatPrintableNutrition(
    locale,
    nutritionData?.[value.nutritionBasis] || null,
    value.nutritionBasis,
    servingLabel
  );

  // Each reorderable section renders to its own node (or null when empty) — order
  // is driven entirely by value.sectionOrder, not by where these appear below.
  const sectionNodes = {
    netWeight: value.netWeight && (
      <div className="recipe-print-label__section" key="netWeight" data-testid={`net-weight-label-section-${locale}`}>
        <div className="recipe-print-label__section-title">{copy.netWeight}</div>
        <div className="recipe-print-label__text">{value.netWeight}</div>
      </div>
    ),
    nutriScore: nutriScore?.show_on_label && nutriScore?.letter && (
      <div className="recipe-print-label__section" key="nutriScore" data-testid={`nutriscore-label-section-${locale}`}>
        <NutriScoreLabelStrip letter={nutriScore.letter} label={copy.nutriScore} mode={nutriScoreMode} />
      </div>
    ),
    ingredients: value.ingredients && (
      <div className="recipe-print-label__section" key="ingredients">
        <div className="recipe-print-label__section-title">{copy.ingredients}</div>
        <div className="recipe-print-label__text">{value.ingredients}</div>
      </div>
    ),
    allergens: value.allergens && (
      <div className="recipe-print-label__section" key="allergens">
        <div className="recipe-print-label__section-title">{copy.allergens}</div>
        <div className="recipe-print-label__text">{value.allergens}</div>
      </div>
    ),
    nutrition: nutritionSection.rows.length > 0 && (
      <div className="recipe-print-label__section" key="nutrition">
        <div className="recipe-print-label__section-title">{nutritionSection.heading}</div>
        <div className="recipe-print-label__nutrition">
          {nutritionSection.rows.map((line) => (
            <div className="recipe-print-label__nutrition-line" key={line.key}>
              <span>{line.label}</span>
              <strong>{line.value}</strong>
            </div>
          ))}
        </div>
      </div>
    ),
    cookingMethods: value.cookingMethods.length > 0 && (
      <div className="recipe-print-label__section" key="cookingMethods">
        <div className="recipe-print-label__section-title">{copy.preparation}</div>
        <div className="recipe-print-label__methods">
          {value.cookingMethods.map((method) => {
            const icon = ICON_MAP[method.iconKey] || ICON_MAP.pan;
            const text = locale === 'nl' ? method.text_nl : method.text_en;
            if (!text) return null;

            return (
              <div className="recipe-print-label__method-row" key={method.id} data-testid={`cooking-method-${locale}`}>
                <img src={icon.src} alt="" aria-hidden="true" className="recipe-print-label__method-icon" />
                <span className="recipe-print-label__method-text">{text}</span>
              </div>
            );
          })}
        </div>
      </div>
    ),
    storage: value.storage && (
      <div className="recipe-print-label__section" key="storage">
        <div className="recipe-print-label__section-title">{copy.storage}</div>
        <div className="recipe-print-label__text">{value.storage}</div>
      </div>
    ),
    extraNotes: value.extraNotes && (
      <div className="recipe-print-label__section" key="extraNotes">
        <div className="recipe-print-label__section-title">{value.extraNotesTitle || copy.extraNotes}</div>
        <div className="recipe-print-label__text">{value.extraNotes}</div>
      </div>
    ),
  };
  const sectionOrder = Array.isArray(value.sectionOrder) ? value.sectionOrder : DEFAULT_LABEL_SECTION_ORDER;

  return (
    <div
      className="recipe-print-label"
      ref={ref}
      data-testid={`printable-label-${locale}`}
      aria-label={`${recipeName || 'Recipe'} ${copy.previewTitle}`}
    >
      <div className="recipe-print-label__brand-row">
        <img
          src={isColorMode ? tabliyaLogoColor : tabliyaLogo}
          alt="Tabliya"
          className="recipe-print-label__brand recipe-print-label__brand--tabliya"
        />
        <img
          src={halalLogo}
          alt="Halal"
          className="recipe-print-label__brand recipe-print-label__brand--halal"
        />
      </div>

      {value.productName && <div className="recipe-print-label__product-name">{value.productName}</div>}

      {sectionOrder.map((key) => sectionNodes[key] || null)}

      <div className="recipe-print-label__company" data-testid={`company-label-section-${locale}`}>
        <div className="recipe-print-label__section-title">{copy.manufacturer}</div>
        <div className="recipe-print-label__text">
          <div>{PRINTABLE_LABEL_COMPANY_DETAILS.name}</div>
        </div>
      </div>

      {value.expiry && (
        <div className="recipe-print-label__expiry">
          {copy.expiry}: {value.expiry}
        </div>
      )}
    </div>
  );
});

// Display name for each reorderable section, sourced from the same bilingual
// copy object used for the printed section headings — so the name shown in
// the drag list always matches what actually prints on the label.
function sectionDisplayName(copy, key) {
  switch (key) {
    case 'netWeight': return copy.netWeight;
    case 'nutriScore': return copy.nutriScore;
    case 'ingredients': return copy.ingredients;
    case 'allergens': return copy.allergens;
    case 'nutrition': return copy.nutrition;
    case 'cookingMethods': return copy.preparation;
    case 'storage': return copy.storage;
    case 'extraNotes': return copy.extraNotes;
    default: return key;
  }
}

function LanguageEditor({ locale, value, onChange, sectionOrder, onReorderSection }) {
  const { t } = useTranslation();
  const copy = PRINTABLE_LABEL_LOCALES[locale] || PRINTABLE_LABEL_LOCALES.en;

  const handleDragEnd = useCallback((result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    onReorderSection(result.source.index, result.destination.index);
  }, [onReorderSection]);

  return (
    <div className="recipe-label-editor__language">
      <div className="recipe-label-editor__language-title">{copy.previewTitle}</div>

      <Form.Group className="mb-3 recipe-label-editor__section-order">
        <Form.Label>{t('recipes.printableLabel.sectionOrder')}</Form.Label>
        <p className="mb-2 text-muted small">{t('recipes.printableLabel.sectionOrderHint')}</p>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={`label-sections-${locale}`}>
            {(provided) => (
              <ul
                className="recipe-label-editor__section-order-list"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {sectionOrder.map((key, idx) => (
                  <Draggable key={key} draggableId={`${locale}-${key}`} index={idx}>
                    {(dragProvided, snapshot) => (
                      <li
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className="recipe-label-editor__section-order-item"
                        style={{
                          ...dragProvided.draggableProps.style,
                          background: snapshot.isDragging ? '#f0f4ff' : undefined,
                        }}
                      >
                        <span
                          {...dragProvided.dragHandleProps}
                          className="recipe-label-editor__section-order-handle"
                          aria-label={t('recipes.printableLabel.dragHandleAria')}
                        >
                          <i className="fa-solid fa-grip-vertical" />
                        </span>
                        <span>{sectionDisplayName(copy, key)}</span>
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{copy.productName}</Form.Label>
        <Form.Control
          value={value.productName}
          onChange={(event) => onChange('productName', event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{copy.netWeight}</Form.Label>
        <Form.Control
          aria-label={copy.netWeight}
          value={value.netWeight || ''}
          onChange={(event) => onChange('netWeight', event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{copy.ingredients}</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={value.ingredients}
          onChange={(event) => onChange('ingredients', event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{copy.allergens}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={value.allergens}
          onChange={(event) => onChange('allergens', event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{copy.storage}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={value.storage}
          onChange={(event) => onChange('storage', event.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-0">
        <Form.Label>{copy.extraNotesTitle}</Form.Label>
        <Form.Control
          className="mb-2"
          value={value.extraNotesTitle}
          onChange={(event) => onChange('extraNotesTitle', event.target.value)}
        />
        <Form.Label>{copy.extraNotes}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={value.extraNotes}
          onChange={(event) => onChange('extraNotes', event.target.value)}
        />
      </Form.Group>
    </div>
  );
}

function readSavedEditorState(storageKey) {
  if (!storageKey || typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function RecipePrintableLabelsWorkspace({ recipe, materials, nutrition, servingLabel, portionWeightG }) {
  const materialsById = useMemo(() => createMaterialsById(materials), [materials]);
  const hasFoodItems = useMemo(() => recipeHasFoodItems(recipe, materialsById), [recipe, materialsById]);
  const totalFoodWeightG = useMemo(() => computeRecipeFoodWeightG(recipe, materialsById), [recipe, materialsById]);
  const nutritionData = useMemo(
    () => calculatePrintableLabelNutritionData(nutrition, totalFoodWeightG, portionWeightG),
    [nutrition, totalFoodWeightG, portionWeightG]
  );
  const initialState = useMemo(
    () => buildPrintableLabelInitialState(recipe, materialsById),
    [recipe, materialsById]
  );
  const storageKey = useMemo(
    () => (recipe?.id ? buildPrintableLabelStorageKey(recipe.id) : null),
    [recipe?.id]
  );
  const { t } = useTranslation();
  const [editorState, setEditorState] = useState(() => initialState);
  const [nutriScoreMode, setNutriScoreMode] = useState(PRINTABLE_NUTRISCORE_MODES.BLACK_AND_WHITE);
  const recipeIdRef = useRef(null);
  const sectionOrderFetchIdRef = useRef(null);
  const nextMethodIdRef = useRef(1);
  const enLabelRef = useRef(null);
  const nlLabelRef = useRef(null);

  useEffect(() => {
    if (!recipe?.id) return;
    if (recipeIdRef.current === recipe.id) return;

    const savedState = readSavedEditorState(storageKey);
    const mergedState = mergePrintableLabelState(initialState, savedState);

    recipeIdRef.current = recipe.id;
    nextMethodIdRef.current = mergedState.cookingMethods.reduce(
      (maxId, method) => Math.max(maxId, Number(method?.id) || 0),
      0
    ) + 1;
    setEditorState(mergedState);
  }, [initialState, recipe?.id, storageKey]);

  // Section order is persisted on the backend (recipe_labels), unlike the rest of
  // this editor's fields which stay localStorage-only — see audit notes. Fetch it
  // once per recipe and overlay it onto whatever localStorage/default order is
  // already in state; a failure here just keeps that local order, no toast (this
  // is a background sync, not a user-initiated action).
  useEffect(() => {
    if (!recipe?.id) return;
    if (sectionOrderFetchIdRef.current === recipe.id) return;
    sectionOrderFetchIdRef.current = recipe.id;

    let cancelled = false;
    Promise.resolve(getRecipeLabel(recipe.id))
      .then((res) => {
        if (cancelled) return;
        const fetchedOrder = res?.data?.sectionOrder;
        if (!fetchedOrder) return;
        setEditorState((current) => ({
          ...current,
          sectionOrder: {
            en: sanitizeSectionOrder(fetchedOrder.en),
            nl: sanitizeSectionOrder(fetchedOrder.nl),
          },
        }));
      })
      .catch(() => {
        // Keep the existing local order; backend sync is best-effort.
      });

    return () => {
      cancelled = true;
    };
  }, [recipe?.id]);

  useEffect(() => {
    if (!storageKey || !editorState || typeof window === 'undefined') return;

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(editorState));
    } catch {
      // Ignore storage failures and keep in-memory editing stable.
    }
  }, [editorState, storageKey]);

  const handleLanguageFieldChange = useCallback((locale, field, nextValue) => {
    setEditorState((current) => ({
      ...current,
      [locale]: {
        ...current[locale],
        [field]: nextValue,
      },
    }));
  }, []);

  const handleExpiryChange = useCallback((nextValue) => {
    setEditorState((current) => ({
      ...current,
      expiry: nextValue,
    }));
  }, []);

  const handleNutritionBasisChange = useCallback((nextValue) => {
    setEditorState((current) => ({
      ...current,
      nutritionBasis: nextValue,
    }));
  }, []);

  const addCookingMethod = useCallback(() => {
    const id = nextMethodIdRef.current;
    nextMethodIdRef.current += 1;

    setEditorState((current) => ({
      ...current,
      cookingMethods: [...current.cookingMethods, createCookingMethod(id)],
    }));
  }, []);

  const updateCookingMethod = useCallback((methodId, field, nextValue) => {
    setEditorState((current) => ({
      ...current,
      cookingMethods: current.cookingMethods.map((method) => (
        method.id === methodId ? { ...method, [field]: nextValue } : method
      )),
    }));
  }, []);

  const removeCookingMethod = useCallback((methodId) => {
    setEditorState((current) => ({
      ...current,
      cookingMethods: current.cookingMethods.filter((method) => method.id !== methodId),
    }));
  }, []);

  const handleSectionReorder = useCallback((locale, sourceIndex, destIndex) => {
    const currentOrder = editorState.sectionOrder[locale];
    const nextOrder = Array.from(currentOrder);
    const [moved] = nextOrder.splice(sourceIndex, 1);
    nextOrder.splice(destIndex, 0, moved);

    setEditorState((current) => ({
      ...current,
      sectionOrder: { ...current.sectionOrder, [locale]: nextOrder },
    }));

    if (recipe?.id) {
      updateRecipeLabel(recipe.id, { sectionOrder: { [locale]: nextOrder } }).catch(() => {
        toast.error(t('recipes.printableLabel.saveOrderFailed'));
      });
    }
  }, [editorState.sectionOrder, recipe?.id, t]);

  const syncFromRecipe = useCallback(() => {
    const resetState = mergePrintableLabelState(initialState, {
      cookingMethods: [],
    });
    nextMethodIdRef.current = 1;
    setEditorState(resetState);
    // Sync-from-recipe resets every customizable field back to computed defaults
    // (see mergePrintableLabelState above) — section order is treated the same
    // way for consistency, and that reset is persisted to the backend too.
    if (recipe?.id) {
      updateRecipeLabel(recipe.id, {
        sectionOrder: { en: resetState.sectionOrder.en, nl: resetState.sectionOrder.nl },
      }).catch(() => {
        toast.error(t('recipes.printableLabel.saveOrderFailed'));
      });
    }
    toast.success('Printable label fields synced from recipe');
  }, [initialState, recipe?.id, t]);

  const downloadLabel = useCallback(async (locale) => {
    const targetRef = locale === 'nl' ? nlLabelRef : enLabelRef;

    try {
      await downloadElementAsPng(
        targetRef.current,
        buildLabelDownloadFileName(recipe?.name, locale),
        { colorMode: nutriScoreMode }
      );
    } catch {
      toast.error('Failed to download printable label');
    }
  }, [nutriScoreMode, recipe?.name]);

  if (!hasFoodItems) {
    return (
      <div className="recipe-print-workspace recipe-print-workspace--empty" data-testid="printable-labels-disabled">
        <div className="recipe-print-workspace__header">
          <div>
            <h5 className="mb-1">{PRINTABLE_LABEL_LOCALES.en.workspaceTitle}</h5>
            <p className="mb-0 text-muted">
              Printable food labels are only available for recipes with food ingredients.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const previewValue = {
    en: {
      ...editorState.en,
      expiry: editorState.expiry,
      nutritionBasis: editorState.nutritionBasis,
      cookingMethods: editorState.cookingMethods,
      sectionOrder: editorState.sectionOrder.en,
    },
    nl: {
      ...editorState.nl,
      expiry: editorState.expiry,
      nutritionBasis: editorState.nutritionBasis,
      cookingMethods: editorState.cookingMethods,
      sectionOrder: editorState.sectionOrder.nl,
    },
  };

  return (
    <div className="recipe-print-workspace" data-testid="printable-labels-workspace">
      <div className="recipe-print-workspace__header">
        <div>
          <h5 className="mb-1">{PRINTABLE_LABEL_LOCALES.en.workspaceTitle}</h5>
          <p className="mb-0 text-muted">
            Live black-and-white label previews for export. Use Sync from recipe when you want to refresh defaults.
          </p>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={syncFromRecipe}>
          <i className="fa-solid fa-rotate me-1" /> Sync from recipe
        </Button>
      </div>

      <div className="recipe-label-editor">
        <div className="recipe-label-editor__grid">
          <LanguageEditor
            locale="en"
            value={editorState.en}
            onChange={(field, nextValue) => handleLanguageFieldChange('en', field, nextValue)}
            sectionOrder={editorState.sectionOrder.en}
            onReorderSection={(sourceIndex, destIndex) => handleSectionReorder('en', sourceIndex, destIndex)}
          />

          <LanguageEditor
            locale="nl"
            value={editorState.nl}
            onChange={(field, nextValue) => handleLanguageFieldChange('nl', field, nextValue)}
            sectionOrder={editorState.sectionOrder.nl}
            onReorderSection={(sourceIndex, destIndex) => handleSectionReorder('nl', sourceIndex, destIndex)}
          />
        </div>

        <div className="recipe-label-editor__shared">
          <Form.Group className="mb-3 recipe-label-editor__basis-field">
            <Form.Label>{PRINTABLE_LABEL_LOCALES.en.basisLabel}</Form.Label>
            <Form.Select
              aria-label={PRINTABLE_LABEL_LOCALES.en.basisLabel}
              value={editorState.nutritionBasis}
              onChange={(event) => handleNutritionBasisChange(event.target.value)}
            >
              <option value="per100g">{PRINTABLE_LABEL_LOCALES.en.basisPer100g}</option>
              <option value="perServing">{PRINTABLE_LABEL_LOCALES.en.basisPerServing}</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3 recipe-label-editor__expiry-field">
            <Form.Label>{PRINTABLE_LABEL_LOCALES.en.expiry} / {PRINTABLE_LABEL_LOCALES.nl.expiry}</Form.Label>
            <Form.Control
              value={editorState.expiry}
              placeholder="e.g. 31-12-2026"
              onChange={(event) => handleExpiryChange(event.target.value)}
            />
          </Form.Group>

          <div className="recipe-label-editor__methods-header">
            <div>
              <div className="recipe-label-editor__language-title mb-1">Cooking methods</div>
              <p className="mb-0 text-muted">Each method gets its own small icon and bilingual instruction line.</p>
            </div>
            <Button variant="outline-dark" size="sm" onClick={addCookingMethod}>
              <i className="fa-solid fa-plus me-1" /> Add method
            </Button>
          </div>

          {editorState.cookingMethods.length === 0 && (
            <div className="recipe-label-editor__methods-empty text-muted">
              No cooking methods yet.
            </div>
          )}

          {editorState.cookingMethods.map((method) => (
            <div className="recipe-label-editor__method-card" key={method.id}>
              <Form.Group>
                <Form.Label>Icon</Form.Label>
                <Form.Select
                  value={method.iconKey}
                  onChange={(event) => updateCookingMethod(method.id, 'iconKey', event.target.value)}
                >
                  {ICON_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>Instruction EN</Form.Label>
                <Form.Control
                  aria-label="Instruction EN"
                  value={method.text_en}
                  onChange={(event) => updateCookingMethod(method.id, 'text_en', event.target.value)}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Instructie NL</Form.Label>
                <Form.Control
                  aria-label="Instructie NL"
                  value={method.text_nl}
                  onChange={(event) => updateCookingMethod(method.id, 'text_nl', event.target.value)}
                />
              </Form.Group>

              <div className="recipe-label-editor__method-actions">
                <Button variant="outline-danger" size="sm" onClick={() => removeCookingMethod(method.id)}>
                  <i className="fa-solid fa-trash me-1" /> Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="recipe-print-workspace__previews">
        <div className="recipe-print-workspace__preview-card">
          <div className="recipe-print-workspace__preview-toolbar">
            <span>{PRINTABLE_LABEL_LOCALES.en.previewTitle}</span>
            <div className="recipe-print-workspace__download-controls">
              <Form.Check
                type="switch"
                id="printable-label-nutriscore-color-mode-en"
                className="recipe-print-workspace__download-mode"
                label={nutriScoreMode === PRINTABLE_NUTRISCORE_MODES.COLOR ? 'Color' : 'Black & White'}
                aria-label="Nutri-Score download color mode"
                checked={nutriScoreMode === PRINTABLE_NUTRISCORE_MODES.COLOR}
                onChange={(event) => setNutriScoreMode(
                  event.target.checked
                    ? PRINTABLE_NUTRISCORE_MODES.COLOR
                    : PRINTABLE_NUTRISCORE_MODES.BLACK_AND_WHITE
                )}
              />
              <Button variant="outline-secondary" size="sm" onClick={() => downloadLabel('en')}>
                <i className="fa-solid fa-download me-1" /> Download EN
              </Button>
            </div>
          </div>
          <PrintableRecipeLabel
            ref={enLabelRef}
            locale="en"
            value={previewValue.en}
            nutritionData={nutritionData}
            recipeName={recipe?.name}
            servingLabel={servingLabel}
            nutriScore={{
              letter: recipe?.nutriscore_letter,
              score: recipe?.nutriscore_score,
              show_on_label: recipe?.nutriscore_show_on_label,
            }}
            nutriScoreMode={nutriScoreMode}
          />
        </div>

        <div className="recipe-print-workspace__preview-card">
          <div className="recipe-print-workspace__preview-toolbar">
            <span>{PRINTABLE_LABEL_LOCALES.nl.previewTitle}</span>
            <div className="recipe-print-workspace__download-controls">
              <Form.Check
                type="switch"
                id="printable-label-nutriscore-color-mode-nl"
                className="recipe-print-workspace__download-mode"
                label={nutriScoreMode === PRINTABLE_NUTRISCORE_MODES.COLOR ? 'Color' : 'Black & White'}
                aria-label="Nutri-Score download color mode"
                checked={nutriScoreMode === PRINTABLE_NUTRISCORE_MODES.COLOR}
                onChange={(event) => setNutriScoreMode(
                  event.target.checked
                    ? PRINTABLE_NUTRISCORE_MODES.COLOR
                    : PRINTABLE_NUTRISCORE_MODES.BLACK_AND_WHITE
                )}
              />
              <Button variant="outline-secondary" size="sm" onClick={() => downloadLabel('nl')}>
                <i className="fa-solid fa-download me-1" /> Download NL
              </Button>
            </div>
          </div>
          <PrintableRecipeLabel
            ref={nlLabelRef}
            locale="nl"
            value={previewValue.nl}
            nutritionData={nutritionData}
            recipeName={recipe?.name}
            servingLabel={servingLabel}
            nutriScore={{
              letter: recipe?.nutriscore_letter,
              score: recipe?.nutriscore_score,
              show_on_label: recipe?.nutriscore_show_on_label,
            }}
            nutriScoreMode={nutriScoreMode}
          />
        </div>
      </div>
    </div>
  );
}
